import { memo, useEffect, useMemo, useState } from 'react';
import {
  Badge,
  ButtonSelect,
  Icon,
  InlineMessage,
  InputField,
  Link as DSLink,
  MenuMulti,
  type MenuMultiOption,
} from '@saviynt/design-system';
import { useIntl } from 'react-intl';

import useDebounce from '../../../../shared/hooks/useDebounce';
import { CHAR_LIMIT_FOR_SEARCH, F_KEY_ST, TOKENS } from '../../constants';
import {
  useManagePageDispatch,
  useManagePageState,
} from '../../contexts/ManagePageContext';
import { useDebouncedFn } from '../../hooks/useDebounceFn';
import { useQueryMap } from '../../hooks/useQueryMap';
import {
  capitalizeFirstLetter,
  getNoDataText,
} from '../../utils/utilManagepage';

import './ManagePageFilter.css';

/**
 * @description Memoised search and filter bar for the Agent Management list.
 * Renders a debounced search input (min {@link CHAR_LIMIT_FOR_SEARCH} chars) and a
 * collapsible filter panel. Filter configs are sourced from {@link useManagePageState}
 * and populated via paginated API calls through {@link useQueryMap}.
 *
 * Filter dropdowns support infinite scroll (next page fetched on scroll-to-bottom),
 * local search-text filtering on each dropdown (checked options always remain visible),
 * individual "Clear Selection" links, and a global "Clear" link when any filter is
 * active. A dot badge on the Filter button indicates active selections.
 *
 * Search and filter changes propagate to the shared ManagePage context
 * (`searchText`, `activeFilters`) which drives the agent list re-fetch.
 *
 * @returns Search input and collapsible multi-select filter controls.
 */
function ManagePageFilter() {
  const intl = useIntl();
  const dispatch = useManagePageDispatch();
  const { filters, isFilterBar, isGlobalLoading } = useManagePageState();

  // States
  const [searchValue, setSearchValue] = useState('');

  // Constants
  const isBadgeIcon = useMemo(
    () => filters.some((f) => f.options.some((o) => o.isChecked)),
    [filters]
  );
  const isClrDisabled = useMemo(
    () => filters.some((f) => f.isLoading),
    [filters]
  );

  // Hooks
  const debouncedSearchValue = useDebounce<string>(searchValue, 300);
  const { getQuery } = useQueryMap(filters);

  // Utils
  const loadFilterOptions = async (fk: string, isInit: boolean = false) => {
    const q = getQuery(fk);

    if (!q) {
      return;
    }

    dispatch((state) => ({
      filters: state.filters.map((f) =>
        f.value === fk ? { ...f, isLoading: true } : f
      ),
    }));

    try {
      const { data } = isInit ? await q.refetch() : await q.fetchNextPage();
      const pages = data?.pages ?? [];

      // Guard against empty pages (e.g., when API returns error)
      if (pages.length === 0) {
        throw Error(`No pages returned for filter ${fk}`);
      }

      const lp = pages[pages.length - 1];
      const fo = pages.flatMap((p) =>
        p.options.map((o) => ({
          label:
            fk === F_KEY_ST
              ? (capitalizeFirstLetter(o.label) ?? o.label)
              : o.label,
          value: o.value,
          isChecked: false,
        }))
      );

      dispatch((state) => ({
        filters: state.filters.map((f) => {
          if (f.value === fk) {
            const omap = new Map(fo.map((o) => [o.value, { ...o }]));
            const cops = f.options.filter((o) => o.isChecked);

            cops.forEach((o) => {
              if (o.isChecked && omap.has(o.value)) {
                omap.get(o.value)!.isChecked = true;

                return;
              }

              if (o.isChecked) {
                omap.set(o.value, { ...o, isChecked: true });
              }
            });

            return {
              ...f,
              options: Array.from(omap.values()),
              isLoading: false,
              hasMoreOptions: lp.pagination.hasNextPage ?? false,
            };
          }

          return f;
        }),
      }));
    } catch {
      dispatch((state) => ({
        filters: state.filters.map((f) =>
          f.value === fk
            ? { ...f, isError: true, isLoading: false, hasMoreOptions: false }
            : f
        ),
      }));
    }
  };

  const debouncedLoadFilterOptions = useDebouncedFn(loadFilterOptions, 300);

  const handleFilterChange = (fk: string, so: MenuMultiOption[]) =>
    dispatch((state) => ({
      filters: state.filters.map((f) =>
        f.value === fk
          ? {
              ...f,
              options: f.options.map((o) => ({
                ...o,
                isChecked: so.some((_o) => _o.value === o.value),
              })),
            }
          : f
      ),
      activeFilters: { ...state.activeFilters, [fk]: so.map((_o) => _o.value) },
    }));

  const handleFooterLink2Click = (fk: string) =>
    dispatch((state) => ({
      filters: state.filters.map((f) =>
        f.value === fk
          ? {
              ...f,
              options: f.options.map((o) => ({
                ...o,
                isChecked: false,
              })),
            }
          : f
      ),
      activeFilters: { ...state.activeFilters, [fk]: [] },
    }));

  const handleClearBtn = () =>
    dispatch((state) => ({
      filters: state.filters.map((f) => ({
        ...f,
        options: f.options.map((o) => ({
          ...o,
          isChecked: false,
        })),
      })),
      activeFilters: {},
    }));

  const handleScroll = (e: React.UIEvent<HTMLUListElement>, fk: string) => {
    const target = e.target as HTMLUListElement;
    const { scrollTop, scrollHeight, clientHeight } = target;
    const tf = filters.find((f) => f.value === fk) ?? { hasMoreOptions: false };

    if (scrollHeight - scrollTop === clientHeight && tf.hasMoreOptions) {
      debouncedLoadFilterOptions(fk);
    }
  };

  // Effects
  useEffect(() => {
    if (
      searchValue.trim().length <= CHAR_LIMIT_FOR_SEARCH &&
      searchValue.trim().length > 0
    ) {
      return;
    }

    dispatch({ searchText: debouncedSearchValue });
  }, [debouncedSearchValue, dispatch]);

  useEffect(() => {
    filters.forEach((f) => {
      loadFilterOptions(f.value, true);
    });
  }, []);

  return (
    <div className='ManagePageFilter'>
      <div className='ManagePageFilter-controls'>
        <div className='ManagePageFilter-row'>
          <InputField
            id='agent-search-input'
            name='search'
            size='medium'
            placeholder={intl.formatMessage({
              id: '01QJoj',
              defaultMessage: 'Search by name',
              description: 'Search input placeholder',
            })}
            kind='search'
            value={searchValue}
            setValue={setSearchValue}
            prefixIcon={
              <Icon kind='Search' size='small' color={TOKENS.CF_NEU_STL} />
            }
            isCritical={
              searchValue.trim().length <= CHAR_LIMIT_FOR_SEARCH &&
              searchValue.trim().length > 0
            }
            CriticalHelperText={
              <InlineMessage
                text={intl.formatMessage({
                  id: 'rMplSY',
                  defaultMessage: 'Type more than 2 characters to see results.',
                  description: 'Type more than 2 characters to see results.',
                })}
                colorTheme='critical'
                size='small'
                leftIcon={<Icon kind='AlertCriticalFilled' />}
              />
            }
            classNames={{ container: 'ManagePageFilter-search' }}
          />
          <ButtonSelect
            label={intl.formatMessage({
              id: 'vYViQu',
              defaultMessage: 'Filter',
              description: 'Filter',
            })}
            onClick={() => dispatch({ isFilterBar: !isFilterBar })}
            isOpen={isFilterBar}
            size='medium'
            prefixIcon={<Icon kind='Filter' />}
            BadgeComp={
              isBadgeIcon ? (
                <Badge kind='dot' color='primary' size='small' />
              ) : undefined
            }
            isVisualCueEnabled={false}
          />
        </div>
      </div>
      <div
        className='ManagePageFilter-pillWrapper'
        data-open={isFilterBar}
        aria-hidden={!isFilterBar}
        {...({ inert: !isFilterBar ? '' : undefined } as Record<
          string,
          string | undefined
        >)}>
        <div className='ManagePageFilter-pillRow'>
          {filters.map((f) => (
            <MenuMulti
              key={f.value}
              label={f.label}
              size='large'
              options={f.options.filter(
                (o) =>
                  o.isChecked ||
                  o.label
                    .toLowerCase()
                    .includes((f.searchText ?? '').toLowerCase())
              )}
              trigger={
                <ButtonSelect
                  kind='pill'
                  label=''
                  isDisabled={f.isLoading || isGlobalLoading}
                />
              }
              onChange={(so) => {
                handleFilterChange(f.value, so ?? []);
              }}
              heightToShowScrollbar='16rem'
              minOptionsForFooter={6}
              minOptionsForSearch={0}
              noDataText={getNoDataText(
                intl,
                f.isError,
                f.isLoading,
                f.searchText
              )}
              onScroll={(e) => handleScroll(e, f.value)}
              footerLink1Text=' '
              footerLink2Text={intl.formatMessage({
                id: 'sOTYaP',
                defaultMessage: 'Clear Selection',
                description: 'Clear Selection',
              })}
              onFooterLink2Click={() => handleFooterLink2Click(f.value)}
              isFilteringDisabled
              showOptionLabelOnHover
              isLoading={f.isLoading}
              loadingText={intl.formatMessage({
                id: 'd1eDsY',
                defaultMessage: 'Loading...',
                description: 'Loading...',
              })}
              onSearchTermChange={(v: string) => {
                dispatch((state) => ({
                  filters: state.filters.map((_f) =>
                    _f.value === f.value ? { ..._f, searchText: v ?? '' } : _f
                  ),
                }));

                // Will be activated later when API is working properly
                // debouncedLoadFilterOptions(f.value, true);
              }}
              isPortalEnabled
            />
          ))}
          {isBadgeIcon && (
            <DSLink
              text={intl.formatMessage({
                id: 'e9OvzW',
                defaultMessage: 'Clear',
                description: 'Clear',
              })}
              kind='button'
              onClick={handleClearBtn}
              isDisabled={isClrDisabled || isGlobalLoading}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default memo(ManagePageFilter);
